import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Button from "../Button";
import SimpleModal from "./SimpleModal";
import Loading from "../Loading";
import mfa from "../../services/mfa";
import {
  Title,
  SubTitle,
  Label,
  SubLabel,
  Input,
  QRContainer,
  QRImageContainer,
} from "./styles";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  handleNextModal: () => void;
  userName: string;
  password: string;
  setBackupCode: (code: string) => void;
  rememberDevice?: boolean;
}

const validationSchema = Yup.object().shape({
  token: Yup.string()
    .required("Este campo es requerido")
    .matches(/^[0-9]+$/, "Debe contener solo números")
    .length(6, "Debe tener 6 dígitos"),
});

const QRCodeModal: React.FC<Props> = ({
  isOpen,
  toggle,
  handleNextModal,
  userName,
  password,
  setBackupCode,
  rememberDevice = false,
}) => {
  const [qrBase64, setQrBase64] = useState<string>("");
  const [manualCode, setManualCode] = useState<string>("");
  const [secretKey, setSecretKey] = useState<string>("");
  const [loadingQR, setLoadingQR] = useState<boolean>(false);
  const tokenInputRef = useRef<HTMLInputElement>(null);

  const loadQRCode = async () => {
	setLoadingQR(true);
	try {
	  const response = await mfa.getQRCode({ userName, password });
	  
	  setQrBase64(response.codigoQr);
	  const rawKey = response.key.replace(/\s/g, "");
	  setSecretKey(rawKey);
	  setManualCode(response.key.replace(/(.{4})/g, "$1 ").trim());
	  setBackupCode(response.backupCode);
	} catch (error: any) {
	  toast.error(error.message || "Error al cargar el código QR");
	} finally {
	  setLoadingQR(false);
	}
  };

  const getOtpAuthUrl = () => {
    if (!secretKey || !userName) return "";
    const issuer = "Legales";
    const accountName = encodeURIComponent(userName);
    const issuerParam = encodeURIComponent(issuer);
    return `otpauth://totp/${issuerParam}:${accountName}?secret=${secretKey}&issuer=${issuerParam}`;
  };

  const handleOpenInApp = () => {
    const otpAuthUrl = getOtpAuthUrl();
    if (otpAuthUrl) {
      window.location.href = otpAuthUrl;
      toast.info("Abriendo aplicación de autenticación...");
    } else {
      toast.error("No se pudo generar el enlace");
    }
  };
  
  useEffect(() => {
    if (userName && password && isOpen) {
      loadQRCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName, password, isOpen]);

  // Autofocus the token input when QR is loaded and modal is open (desktop only)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isOpen && !loadingQR && qrBase64 && tokenInputRef.current && !isMobile) {
      setTimeout(() => tokenInputRef.current?.focus(), 100);
    }
  }, [isOpen, loadingQR, qrBase64]);


  const handleSubmit = async (values: { token: string }, { setSubmitting, setFieldError }: any) => {
    try {
      const response = await mfa.verifyQRCode({
        token: values.token.replace(/\s/g, ""),
        email: userName,
        password,
      });

      if (response.success) {
        // Store browser_hash if provided for automatic login
        if (response.browser_hash) {
          localStorage.setItem("browserHash", response.browser_hash);
          localStorage.setItem("rememberDevice", "true");
        }
        toast.success("Autenticación de dos factores habilitada");
        handleNextModal();
      } else {
        setFieldError("token", "Código de verificación incorrecto");
        toast.error("Código de verificación incorrecto");
      }
    } catch (error: any) {
      setFieldError("token", "Error al verificar el código");
      toast.error(error.message || "Error al verificar el código");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SimpleModal isOpen={isOpen} onClose={toggle} title="" size="md">
      <div style={{ padding: "24px" }} className="modal-content-responsive">
        <Title>Habilitar autenticación de dos factores</Title>
        <SubTitle style={{ marginBottom: "12px" }}>Escanear el código QR</SubTitle>

        <Formik
          initialValues={{ token: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <QRContainer style={{ marginBottom: "12px" }}>
                <QRImageContainer>
                  {loadingQR ? (
                    <Loading />
                  ) : (
                    <>
                      <img src={qrBase64} alt="QR Code" />
                      <p style={{ fontSize: "13px", margin: "8px 0 0 0" }}>{manualCode}</p>
                      {window.innerWidth <= 768 && (
                        <Button
                          type="button"
                          options={{ skin: "primary", size: "md", type: "filled" }}
                          onClick={handleOpenInApp}
                          disabled={!secretKey}
                          style={{ marginTop: "12px", width: "100%", fontSize: "14px", padding: "10px 16px", fontWeight: "600" }}
                        >
                          Agregar a app de autenticación
                        </Button>
                      )}
                    </>
                  )}
                </QRImageContainer>
                <SubLabel style={{ width: "60%", marginBottom: 0, maxWidth: "100%", fontSize: "13px", lineHeight: "1.3" }}>
                  {window.innerWidth <= 768 
                    ? "Escanea el código QR o haz clic en el botón para agregar automáticamente."
                    : "Escanea el código QR con tu aplicación de autenticación."}
                </SubLabel>
              </QRContainer>

              <div style={{ marginTop: "12px" }}>
                <Label style={{ marginBottom: "6px" }}>Código de verificación</Label>
                <Input
                  type="number"
                  name="token"
                  placeholder="Ingresa el código de 6 dígitos"
                  value={values.token}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    handleChange({ target: { name: 'token', value } });
                  }}
                  onBlur={handleBlur}
                  disabled={isSubmitting || loadingQR}
                  className={errors.token && touched.token ? "error-field" : ""}
                  maxLength={6}
                  ref={tokenInputRef}
                />
                {errors.token && touched.token && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.token}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "16px",
                }}
                className="modal-footer-responsive"
              >
                <Button
                  type="button"
                  options={{ skin: "secondary", size: "md", type: "filled" }}
                  onClick={toggle}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  options={{ skin: "primary", size: "md", type: "filled" }}
                  disabled={isSubmitting || loadingQR}
                >
                  {isSubmitting ? "Verificando..." : "Verificar"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      
      <style>{`
        @media (max-width: 768px) {
          .modal-content-responsive {
            padding: 12px !important;
          }
          .modal-footer-responsive {
            flex-direction: column !important;
            margin-top: 12px !important;
          }
          .modal-footer-responsive button {
            width: 100%;
          }
        }
      `}</style>
    </SimpleModal>
  );
};

export default QRCodeModal;
